<template>
  <div class="common-map-location" :class="{'--fixed-map': showPositionFixedMap !== false}">
    <client-only>
      <div ref="mapRef" class="map-container" :style="`height: ${height}px;`" />
    </client-only>
  </div>
</template>

<script lang="ts" setup>
import {nextTick, PropType, Ref} from 'vue';
import {GoogleMap, GoogleMapMarker, GoogleMapMarkerProp} from '~/@types/google-map';
import {useListingStore} from '~/store/listing';
import {useDevelopmentStore} from '~/store/development';
import {useRoute} from '#app';
import ListingSearchQueryHandler from '~/composables/listingSearchQuery';
import {debounce} from 'lodash';

// PROPS Definitions
//----------------------------------------------------------------------------------------------------------------------
const props = defineProps({
  height: {
    type: Number,
    default: 300,
  },
  markers: {
    type: Array as PropType<GoogleMapMarkerProp[]>,
    default: () => [],
  },
  filterByMapBounds: {
    type: Boolean,
    default: false,
  },
  filterByMapBoundsStore: {
    type: String as PropType<'listing' | 'development'>,
    default: 'listing',
  },
  showPositionFixedMap: {
    type: Boolean,
    default: false,
  },
  zoomLevel: {
    type: Number as PropType<number | null>,
    default: null,
  },
  shouldCenterToMarker: {
    type: Boolean,
    default: false,
  },
});

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
const mapRef: Ref<HTMLElement | null> = ref(null);
const map: Ref<GoogleMap | null> = ref(null);
const mapMarkers: Ref<GoogleMapMarker[]> = ref([]);
const infoWindow: Ref<google.maps.InfoWindow | null> = ref(null);
const filterByMapBoundsStore = props.filterByMapBoundsStore === 'listing' ? useListingStore() : useDevelopmentStore();
const firstBoundsChangedCompleted: Ref<boolean> = ref(false);
const hasMounted: Ref<boolean> = ref(false);
const mapBounds: Ref<string | null> = ref(null);
const infoWindowPopupCausedBoundsChange: Ref<boolean> = ref(false);
const route = useRoute();
const router = useRouter();
const defaultMapBounds = '(-33.547472833751925, -175.27281298419928, -33.547472833751925, 165.74281298419922, -48.00950464048173, 165.74281298419922, -48.00950464048173, -175.27281298419928)';

/**
 * @_MOUNTED hook
 */
onMounted(() => {
  initMap();
});

/**
 * @_WATCH props.markers
 */
watch(() => props.markers, () => {
  setMarkers();
}, { deep: true });

/**
 * @_WATCH props.mapBounds
 */
watch(() => filterByMapBoundsStore.hoveredItemSlug, () => {
  setAnimationToMarker();
});

/**
 * @_WATCH route.query
 */
watch(() => route.query, (newValue) => {
  if (hasMounted.value) {
    if (newValue.mapBounds !== mapBounds.value) {
      if (!firstBoundsChangedCompleted.value) {
        map.value?.setZoom(8);
      }
      setMapBounds();
    }
  }
});

/**
 * Map bounds debounce handler
 */
const handleBoundsChange = debounce(() => {
  boundsChanged();
}, 300);  // 300ms delay

/**
 * Initiate the map
 */
async function initMap () {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  if (mapRef.value && (window) && (window as never).google && (window as never).google.maps) {
    const NEW_ZEALAND_BOUNDS = {
      north: -34.36,
      south: -47.35,
      west: 166.28,
      east: -175.81,
    };

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    const { Map } = await (window as never).google.maps.importLibrary('maps');
    map.value = new Map(mapRef.value as HTMLElement, {
      mapId: '7cfa31762212a2cd',
      gestureHandling: props.showPositionFixedMap === false ? 'auto' : 'greedy',
      fullscreenControl: false,
      restriction: {
          latLngBounds: NEW_ZEALAND_BOUNDS,
          strictBounds: false,
        },
    });
    initMapData();
  } else {
    setTimeout(() => {
      initMap();
    }, 300);
  }
}

/**
 * Initiate the map data
 */
function initMapData () {
  if (map.value) {
    nextTick(() => {
      setMapBounds();
      setMarkers();
      if (props.filterByMapBounds !== false) {
        map.value?.addListener('bounds_changed', handleBoundsChange);
      }
      // If should center to marker is true and only one marker is present then center the map to that marker
      let mapCenter = null;
      if (props.shouldCenterToMarker && props.markers.length === 1 && props.markers[0].position) {
        mapCenter = {lat: props.markers[0].position.lat(), lng: props.markers[0].position.lng()};
      }
      // Set map center
      setTimeout(() => {
        if (mapCenter && map.value) {
          if (props.zoomLevel) {
            map.value?.setZoom(props.zoomLevel);
          }
          map.value.setCenter(mapCenter);
        }
      }, 500);
      setTimeout(() => {
        hasMounted.value = true;
      }, 3000);
    });
  } else {
    setTimeout(() => {
      initMapData();
    }, 300);
  }
}

/**
 * Initiate the map bounds
 */
function setMapBounds () {
  if (map.value && !infoWindowPopupCausedBoundsChange.value) {
    map.value.fitBounds(getBoundsFromUrlQuery());
  }
}

/**
 * Get bounds from url query
 */
function getBoundsFromUrlQuery (forcedBounds?: string) {
  mapBounds.value = route.query.mapBounds ? route.query.mapBounds as string : defaultMapBounds;
  if (forcedBounds) {
    mapBounds.value = forcedBounds;
  }
  let mapBoundsArray = (mapBounds.value.slice(1, -1)).split(',');
  if (mapBoundsArray.length !== 8) {
    mapBoundsArray = (defaultMapBounds.slice(1, -1)).split(',');
  }

  var northEast = { lat: mapBoundsArray[0], lng: mapBoundsArray[1] };
  var southWest = { lat: mapBoundsArray[4], lng: mapBoundsArray[5] };

  // Create LatLng objects for our corners
  var neLatLng = new google.maps.LatLng((northEast.lat as any), (northEast.lng as any));
  var swLatLng = new google.maps.LatLng((southWest.lat as any), (southWest.lng as any));

  // Create a LatLngBounds object
  return new google.maps.LatLngBounds(swLatLng, neLatLng);
}

/**
 * Trigger search when map bounds changed
 */
async function boundsChanged () {
  if (map.value && hasMounted.value && !infoWindowPopupCausedBoundsChange.value) {
    setMapBoundsFromMapObject();

    if (props.filterByMapBoundsStore === 'listing') {
      ListingSearchQueryHandler.set({
        mapBounds: mapBounds.value || '',
      });
    }

    setUrlParams();
  }
}

/**
 * Set map bounds from the map object
 */
function setMapBoundsFromMapObject () {
  if (map.value && hasMounted.value && !infoWindowPopupCausedBoundsChange.value) {
    const bounds = map.value.getBounds();
    if (bounds) {
      const northEast = bounds.getNorthEast();
      const southWest = bounds.getSouthWest();

      const northWest = new google.maps.LatLng(northEast.lat(), southWest.lng());
      const southEast = new google.maps.LatLng(southWest.lat(), northEast.lng());

      mapBounds.value = `(${northEast.lat()}, ${northEast.lng()}, ${northWest.lat()}, ${northWest.lng()}, ${southWest.lat()}, ${southWest.lng()}, ${southEast.lat()}, ${southEast.lng()})`;
    }
  }
}

/**
 * Set URL params
 */
function setUrlParams () {
  if (hasMounted.value && !infoWindowPopupCausedBoundsChange.value) {
    const {query} = route;
    const newQuery = {...query, ...{page: 1, mapBounds: mapBounds.value}};
    router.push({query: newQuery});
  }
}

/**
 * Set markers
 */
function setMarkers (autoTryCount = 0) {
  if (map.value) {
    mapMarkers.value.forEach((marker) => {
      marker.setVisible(false);
      marker.setMap(null);
    });

    // Clear the mapMarkers array
    mapMarkers.value = [];

    // Set markers if props has markers
    if (Array.isArray(props.markers) && props.markers.length > 0 && map.value) {

      props.markers.forEach((marker) => {
        const markerObject: GoogleMapMarker = new (window as any).google.maps.Marker({
          id: marker.id,
          position: marker.position,
          title: marker.title,
          infoWindowContent: marker.infoWindowContent,
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(getSvgString(marker.color)),
            scaledSize: new google.maps.Size(40, 40),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(20, 40),
          },
        });

        markerObject.setMap(map.value);
        if (marker.infoWindowContent) {
          markerObject.addListener('click', () => displayMarkerInfoWindow(markerObject));
        }
        mapMarkers.value.push(markerObject);
      });
    }
  } else if (autoTryCount < 10) {
    setTimeout(() => {
      setMarkers(autoTryCount + 1);
    }, 500);
  }
}

/**
 * Display marker info window
 *
 * @param mapMarker
 */
function displayMarkerInfoWindow (mapMarker: GoogleMapMarker) {
  infoWindowPopupCausedBoundsChange.value = true;
  // Close the info window if already opened
  if (infoWindow.value) {
    infoWindow.value.close();
    infoWindow.value = null;
  }

  infoWindow.value = new google.maps.InfoWindow({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    content: mapMarker.infoWindowContent,
    ariaLabel: 'Test Label',
  });

  infoWindow.value.open({
    anchor: mapMarker,
    map: map.value,
  });

  setTimeout(() => {
    infoWindowPopupCausedBoundsChange.value = false;
  }, 2000);
}

/**
 * Set animation to marker
 */
function setAnimationToMarker () {
  for (const marker of mapMarkers.value) {
    if ((marker as any).id === filterByMapBoundsStore.hoveredItemSlug) {
      marker.setAnimation(google.maps.Animation.BOUNCE);
    } else {
      marker.setAnimation(null);
    }
  }
}

/**
 * Get SVG string
 */
function getSvgString (color = '#222145') {
  return `<svg version="1.2" baseProfile="tiny" xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50" overflow="inherit">
            <path fill="${color || '#222145'}" stroke="white" stroke-width="1" d="M25.015 2.4c-7.8 0-14.121 6.204-14.121 13.854 0 7.652 14.121 32.746 14.121 32.746s14.122-25.094 14.122-32.746c0-7.65-6.325-13.854-14.122-13.854z"/>
          </svg>`;
}
</script>

<style lang="scss">
.common-map-location {
  overflow: hidden !important;
  scroll-behavior: unset !important;
  overflow-scrolling: unset !important;

  &.--fixed-map {
    @media screen and (max-width: $grid-sm) {
      position: fixed;
      width: 100%;
      height: 100%;
      top: 112px;
      left: 0;
      z-index: 1;

      .map-container {
        position: fixed;
        width: 100%;
        height: 100% !important;
      }
    }
  }

  .map-container {
    color: rgba(92, 82, 198, 0.49);

    // Responsive
    @media screen and (max-width: $grid-sm) {
      .gm-style-iw.gm-style-iw-c {
        max-width: 365px !important;
      }
    }
  }
}

.gm-style-iw-d {
  overflow: hidden !important;
}

.gm-style-iw.gm-style-iw-c, .gm-style-iw-d {
  padding: 0 !important;
}

.map-info-window {
  .blocks-listing {
    margin-bottom: 0 !important;
    border-radius: unset !important;

    .__listing-image-wrapper {
      border-radius: unset !important;
    }

    .__icon-info-bar {
      z-index: 9;
    }
  }
}
</style>
